import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  ActivityScope,
  JobType,
  PaymentType,
  DayOfWeek,
  BOOKING_TYPES,
  PAYMENT_FREQUENCIES,
  TARGET_STATUSES,
} from 'us-web-services';

const DAYS_OF_WEEK: { value: DayOfWeek; label: string }[] = [
  { value: 'MONDAY', label: 'M' },
  { value: 'TUESDAY', label: 'T' },
  { value: 'WEDNESDAY', label: 'W' },
  { value: 'THURSDAY', label: 'Th' },
  { value: 'FRIDAY', label: 'F' },
  { value: 'SATURDAY', label: 'Sa' },
  { value: 'SUNDAY', label: 'Su' },
];

export interface ActivityFormValues {
  activityScope: ActivityScope;
  jobType: JobType;
  bookingTypeId: number;
  paymentType: PaymentType;
  paymentFrequencyId: number;
  startDate: Date | null;
  startTime: string;
  durationHours: number;
  targetBookingStatus: string;
  numberOfChildren: number;
  hourlyRate: number;
  autoApproveJob: boolean;
  count: number;
  recurringDays: DayOfWeek[];
}

interface ActivityConfigFormProps {
  values: ActivityFormValues;
  onChange: (updates: Partial<ActivityFormValues>) => void;
  submitting: boolean;
  parentId: string;
  onSubmit: () => void;
  careCreditAvailable: boolean;
}

const ActivityConfigForm: React.FC<ActivityConfigFormProps> = ({
  values,
  onChange,
  submitting,
  parentId,
  onSubmit,
  careCreditAvailable,
}) => {
  const {
    activityScope,
    jobType,
    bookingTypeId,
    paymentType,
    paymentFrequencyId,
    startDate,
    startTime,
    durationHours,
    targetBookingStatus,
    numberOfChildren,
    hourlyRate,
    autoApproveJob,
    count,
    recurringDays,
  } = values;

  return (
    <Card>
      <CardContent>
        <Typography variant='h6' gutterBottom>
          Activity Configuration
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component='fieldset'>
              <FormLabel component='legend'>Activity Scope</FormLabel>
              <RadioGroup
                row
                value={activityScope}
                onChange={(e) => onChange({ activityScope: e.target.value as ActivityScope })}
              >
                <FormControlLabel value='JOB_ONLY' control={<Radio />} label='Job Post Only' />
                <FormControlLabel value='JOB_INTEREST' control={<Radio />} label='Job + Sitter Interest' />
                <FormControlLabel value='JOB_INTEREST_BOOKING' control={<Radio />} label='Job + Interest + Booking' />
                <FormControlLabel value='FULL_FLOW' control={<Radio />} label='Full Flow' />
              </RadioGroup>
            </FormControl>
          </Grid>

          {activityScope === 'FULL_FLOW' && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size='small'>
                <InputLabel>Target Booking Status</InputLabel>
                <Select
                  value={targetBookingStatus}
                  label='Target Booking Status'
                  onChange={(e) => onChange({ targetBookingStatus: e.target.value })}
                >
                  <MenuItem value={TARGET_STATUSES.PENDING}>Pending</MenuItem>
                  <MenuItem value={TARGET_STATUSES.ACCEPTED}>Accepted</MenuItem>
                  <MenuItem value={TARGET_STATUSES.COMPLETED}>Completed</MenuItem>
                  <MenuItem value={TARGET_STATUSES.CANCELLED}>Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl component='fieldset'>
              <FormLabel component='legend'>Job Type</FormLabel>
              <RadioGroup
                row
                value={jobType}
                onChange={(e) => onChange({ jobType: e.target.value as JobType })}
              >
                <FormControlLabel value={JobType.ONE_TIME} control={<Radio />} label='One-time' />
                <FormControlLabel value={JobType.RECURRING} control={<Radio />} label='Recurring' />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size='small'>
              <InputLabel>Booking Type</InputLabel>
              <Select
                value={bookingTypeId}
                label='Booking Type'
                onChange={(e) => onChange({ bookingTypeId: e.target.value as number })}
              >
                <MenuItem value={BOOKING_TYPES.CHILDCARE}>Childcare</MenuItem>
                <MenuItem value={BOOKING_TYPES.PETCARE}>Pet Care</MenuItem>
                <MenuItem value={BOOKING_TYPES.SENIORCARE}>Senior Care</MenuItem>
                <MenuItem value={BOOKING_TYPES.HOUSEHOLD}>Household Services</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl component='fieldset'>
              <FormLabel component='legend'>Payment Type</FormLabel>
              <RadioGroup
                row
                value={paymentType}
                onChange={(e) => onChange({ paymentType: e.target.value as PaymentType })}
              >
                <FormControlLabel value='CASH' control={<Radio />} label='Cash' />
                <FormControlLabel value='CREDIT' control={<Radio />} label='Credit Card' />
                <FormControlLabel
                  value='CARE_CREDIT'
                  control={<Radio />}
                  label={careCreditAvailable ? 'Care Credit (B2B)' : 'Care Credit (B2B) — validate parent first'}
                  disabled={!careCreditAvailable}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size='small'>
              <InputLabel>Payment Frequency</InputLabel>
              <Select
                value={paymentFrequencyId}
                label='Payment Frequency'
                onChange={(e) => onChange({ paymentFrequencyId: e.target.value as number })}
              >
                <MenuItem value={PAYMENT_FREQUENCIES.HOURLY}>Hourly</MenuItem>
                <MenuItem value={PAYMENT_FREQUENCIES.FLAT_RATE}>Flat Rate</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Date & Time
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <DatePicker
              label='Start Date'
              value={startDate}
              onChange={(newValue) => onChange({ startDate: newValue as Date | null })}
              renderInput={(params) => <TextField {...params} size='small' fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label='Start Time'
              type='time'
              value={startTime}
              onChange={(e) => onChange({ startTime: e.target.value })}
              size='small'
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label='Duration (hours)'
              type='number'
              value={durationHours}
              onChange={(e) => onChange({ durationHours: parseInt(e.target.value, 10) || 1 })}
              size='small'
              fullWidth
              inputProps={{ min: 1, max: 12 }}
            />
          </Grid>

          {jobType === JobType.RECURRING && (
            <Grid item xs={12}>
              <FormLabel component='legend'>Recurring Days</FormLabel>
              <ToggleButtonGroup
                value={recurringDays}
                onChange={(_, newDays) => onChange({ recurringDays: newDays })}
                aria-label='recurring days'
                size='small'
              >
                {DAYS_OF_WEEK.map((day) => (
                  <ToggleButton key={day.value} value={day.value}>
                    {day.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Job Details
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label='Number of Children'
              type='number'
              value={numberOfChildren}
              onChange={(e) => onChange({ numberOfChildren: parseInt(e.target.value, 10) || 1 })}
              size='small'
              fullWidth
              inputProps={{ min: 1, max: 10 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label='Hourly Rate ($)'
              type='number'
              value={hourlyRate}
              onChange={(e) => onChange({ hourlyRate: parseInt(e.target.value, 10) || 1 })}
              size='small'
              fullWidth
              inputProps={{ min: 10, max: 100 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label='Count'
              type='number'
              value={count}
              onChange={(e) => onChange({ count: parseInt(e.target.value, 10) || 1 })}
              size='small'
              fullWidth
              inputProps={{ min: 1, max: 10 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Options
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoApproveJob}
                  onChange={(e) => onChange({ autoApproveJob: e.target.checked })}
                />
              }
              label='Auto-approve job post (skip moderation)'
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='contained'
            color='primary'
            onClick={onSubmit}
            disabled={submitting || !parentId}
          >
            {submitting ? 'Generating...' : 'Generate Activity'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ActivityConfigForm;
